import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { QueryFactory } from 'services/api';
import { PagedResultOfVariableListItemDto } from 'services/api/api-client.types';
import { UseQueryResult } from '@tanstack/react-query';
import { useDebounce } from 'use-debounce';
import {
  CascadeLevel,
  CascadeSelection,
} from 'components/uikit/filters/cascading/types';
import { useVariableFilterConfig } from './useVariableFilterConfig';
import { useLocalStorage } from 'helpers/useStorage';
import { queryClient } from 'services/api/query-client-helper';

export type VariablesProviderProps = {
  projectId?: number;
  revisionId?: number;

  /**
   * localStorage key for the search, the filter and the expanded accordions.
   * Kept in memory if not set.
   */
  storageKey?: string;
};

export type ExpandedAccordionsKey = 'manual' | 'system' | 'documents' | number;

export type ExpandedAccordions = Record<ExpandedAccordionsKey, boolean>;

type VariablesViewState = {
  search: string;
  filter: CascadeSelection<string>;
  expanded: ExpandedAccordions;
};

const defaultViewState: VariablesViewState = {
  search: '',
  filter: {},
  expanded: { manual: true, system: true, documents: false },
};

export const VariablesProvider = (
  props: VariablesProviderProps & PropsWithChildren,
) => {
  const [{ search, filter, expanded }, setViewState] = useLocalStorage(
    props.storageKey,
    defaultViewState,
  );
  const [debouncedSearch] = useDebounce(search, 500);

  const { variablesFilterConfig, toSearchFilterParams } =
    useVariableFilterConfig();

  const filterParams = useMemo(() => toSearchFilterParams(filter), [filter]);

  const searchParam = debouncedSearch.trim() ? debouncedSearch : undefined;
  const isNarrowed =
    searchParam !== undefined ||
    Object.values(filterParams).some((param) => param !== undefined);

  const enabled = !!props.projectId && !!props.revisionId;

  const variablesQuery = QueryFactory.VariableQuery.useSearchQuery(
    {
      projectId: props.projectId,
      revisionId: props.revisionId,
      search: searchParam,
      ...filterParams,
    },
    {
      enabled,
    },
  );

  const allVariablesQuery = QueryFactory.VariableQuery.useSearchQuery(
    {
      projectId: props.projectId,
      revisionId: props.revisionId,
    },
    {
      enabled,
    },
  );

  const refreshVariables = useCallback(async () => {
    if (!enabled) return;

    await queryClient.invalidateQueries({
      queryKey: QueryFactory.VariableQuery.searchQueryKey(),
    });
  }, [enabled]);

  const setSearch = (value: string) =>
    setViewState((prev) => ({ ...prev, search: value }));
  const setFilter = (value: CascadeSelection<string>) =>
    setViewState((prev) => ({ ...prev, filter: value }));
  const setIsExpanded = (value: boolean) =>
    setViewState((prev) => ({
      ...prev,
      expanded: { manual: value, system: value, documents: value },
    }));
  const toggleExpanded = (id: ExpandedAccordionsKey, isExpanded: boolean) =>
    setViewState((prev) => ({
      ...prev,
      expanded: { ...prev.expanded, [id]: !isExpanded },
    }));

  return (
    <VariablesContext.Provider
      value={{
        variablesQuery,
        allVariablesQuery,
        refreshVariables,
        debouncedSearch,
        search,
        setSearch,
        filterConfig: variablesFilterConfig,
        filter,
        setFilter,
        isNarrowed,
        expanded,
        setIsExpanded,
        toggleExpanded,
      }}
    >
      {props.children}
    </VariablesContext.Provider>
  );
};

export const useVariablesContext = () => {
  const context = useContext(VariablesContext);
  if (!context) {
    throw new Error(
      'useVariablesContext must be used within a VariablesProvider',
    );
  }
  return context;
};

export type VariablesContextType = {
  /** Variables narrowed by the search and the filter.*/
  variablesQuery: UseQueryResult<PagedResultOfVariableListItemDto, unknown>;

  /**Every variable of the revision, regardless of the search and the filter.*/
  allVariablesQuery: UseQueryResult<PagedResultOfVariableListItemDto, unknown>;

  refreshVariables: () => Promise<void>;

  debouncedSearch: string;
  search: string;
  setSearch: (search: string) => void;

  filterConfig: CascadeLevel<string>;
  filter: CascadeSelection<string>;
  setFilter: (filter: CascadeSelection<string>) => void;

  /** Whether the search or the filter narrows `variablesQuery` down. */
  isNarrowed: boolean;

  /** Expanded state of the variable list accordions. */
  expanded: ExpandedAccordions;
  /** Expands or collapses every accordion. */
  setIsExpanded: (isExpanded: boolean) => void;
  toggleExpanded: (id: ExpandedAccordionsKey, isExpanded: boolean) => void;
};

export const VariablesContext = createContext<VariablesContextType | null>(
  null,
);
